// NEW_FILE_JUSTIFIED: Builder center-column Canvas — section list with dnd-kit reordering
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Plus } from 'lucide-react';
import type { ICanvasProps } from '@/@types/insuranceBuilder';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SectionCard } from './SectionCard';
import { AiExtractionBanner } from './AiExtractionBanner';

export function Canvas({ sections, selection, dispatch, aiKeys, density = 'cozy' }: ICanvasProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.sectionKey === active.id);
    const newIndex = sections.findIndex(s => s.sectionKey === over.id);
    const reordered = [...sections];
    const [moved] = reordered.splice(oldIndex, 1);
    if (moved) reordered.splice(newIndex, 0, moved);
    dispatch({ type: 'REORDER_SECTIONS', orderedKeys: reordered.map(s => s.sectionKey) });
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-4">
        {aiKeys && (
          <AiExtractionBanner
            sectionCount={aiKeys.sectionKeys.size}
            fieldCount={aiKeys.fieldKeys.size}
          />
        )}
        {sections.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <p className="text-muted-foreground mb-3">No sections yet</p>
            <Button variant="outline" onClick={() => dispatch({ type: 'ADD_SECTION', columns: 1 })}>
              <Plus className="w-4 h-4 mr-2" />
              Add a section
            </Button>
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={sections.map(s => s.sectionKey)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map(section => (
              <SectionCard
                key={section.sectionKey}
                section={section}
                isSelected={
                  selection?.kind === 'section' && selection.sectionKey === section.sectionKey
                }
                selection={selection}
                dispatch={dispatch}
                isAiExtracted={aiKeys?.sectionKeys.has(section.sectionKey)}
                aiFieldKeys={aiKeys?.fieldKeys}
                density={density}
              />
            ))}
          </SortableContext>
        </DndContext>
        {sections.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => dispatch({ type: 'ADD_SECTION', columns: 1 })}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add section
          </Button>
        )}
      </div>
    </ScrollArea>
  );
}
